// =============================================================================
// DETALLE DE CONTRATO (capa mock)
// Deriva la ficha completa de cada contrato: condiciones, costos, cuenta
// corriente, check-in, check-out, arreglos, línea de tiempo y documentos.
// Al conectar una base real, esta función se reemplaza por una llamada que
// devuelva la misma forma (ver /app/docs/esquema-datos.md → DetalleContrato).
// =============================================================================

import { addDays, differenceInCalendarDays, parseISO } from "date-fns";
import { COBROS, propiedadPorId } from "./mockData";
import { mesAnio } from "../lib/format";

const HOY = new Date();
HOY.setHours(0, 0, 0, 0);
const iso = (d) => d.toISOString();

const FOTOS = [
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&q=70",
  "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&q=70",
  "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&q=70",
  "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&q=70",
];
const PROVEEDORES = ["Plomería San José", "Electricidad Ramón", "Clima Service SA", "Pinturas Norberto", "Cerrajería Central"];
const AMBIENTES = ["Living-comedor", "Cocina", "Dormitorio principal", "Baño"];

function rng(seed) {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}
function hashStr(str) {
  let h = 7;
  for (const ch of str) h = (h * 31 + ch.charCodeAt(0)) & 0x7fffffff;
  return h;
}

const cache = new Map();

export function detalleContrato(c) {
  if (!c) return null;
  if (!cache.has(c.id)) cache.set(c.id, construir(c));
  return cache.get(c.id);
}

function construir(c) {
  const r = rng(hashStr(c.id));
  const pick = (arr) => arr[Math.floor(r() * arr.length)];
  const entre = (a, b) => a + Math.floor(r() * (b - a + 1));
  const prop = propiedadPorId(c.propiedadId);
  const expensas = prop?.expensas ?? 45000;

  // ---------------- Garantía / depósito ----------------
  const tipoGarantia = pick(["Propietaria", "Seguro de caución", "Recibo de sueldo"]);
  const garantia = {
    tipo: tipoGarantia,
    datos:
      tipoGarantia === "Propietaria"
        ? "Inmueble en CABA a nombre de familiar directo"
        : tipoGarantia === "Seguro de caución"
          ? `Póliza N° ${entre(10000, 99999)} · ${pick(["Aseguradora del Plata", "Seguros Australes", "Caución Express"])} · vigente`
          : "Recibo de sueldo con antigüedad mayor a 1 año",
  };
  const deposito = {
    monto: c.montoMensual,
    estado: c.estado === "Finalizado" ? "Devuelto" : c.estado === "En salida" ? "A devolver" : "Retenido",
  };

  // ---------------- Arreglos ----------------
  const arreglos = [];
  const nArr = c.situacion === "Arreglo en curso" ? 2 : c.estado === "En salida" ? 1 : r() > 0.55 ? 1 : 0;
  const titulosArr = [
    "Pérdida en canilla de cocina",
    "Pintura de dormitorio",
    "Artefacto de baño con fisura",
    "Termotanque no enciende",
    "Ventana no cierra bien",
  ];
  for (let i = 0; i < nArr; i++) {
    const flow =
      c.situacion === "Arreglo en curso" && i === 0
        ? { aprobacion: "Aprobado", estado: "En curso" }
        : c.estado === "En salida" && i === 0
          ? { aprobacion: "Pendiente", estado: "Presupuestado" }
          : r() > 0.5
            ? { aprobacion: "Aprobado", estado: "Terminado" }
            : { aprobacion: pick(["Pendiente", "Rechazado"]), estado: "Presupuestado" };
    arreglos.push({
      id: `${c.id}-arr-${i + 1}`,
      titulo: pick(titulosArr),
      ambiente: pick(AMBIENTES),
      fechaReporte: iso(addDays(HOY, -entre(5, 120))),
      urgencia: pick(["Alta", "Media", "Baja"]),
      paga: pick(["Inquilino", "Propietario"]),
      presupuesto: entre(45, 320) * 1000,
      aprobacion: flow.aprobacion,
      proveedor: pick(PROVEEDORES),
      estado: flow.estado,
      fotos: { antes: FOTOS[entre(0, 3)], despues: flow.estado === "Terminado" ? FOTOS[entre(0, 3)] : null },
    });
  }

  // ---------------- Cuenta corriente (últimos 6 meses) ----------------
  const cobrosC = COBROS.filter((x) => x.contratoId === c.id);
  const cuenta = [];
  let saldoAcumulado = 0;
  for (let i = 5; i >= 0; i--) {
    const fechaMes = addDays(HOY, -30 * i);
    let estadoMes = "Pagado";
    let cobrado = c.montoMensual;
    let debe = 0;
    let punitorios = 0;
    let arr = 0;
    if (i === 0 && cobrosC[0]) {
      estadoMes = cobrosC[0].estado;
      if (estadoMes !== "Pagado") {
        cobrado = 0;
        debe = c.montoMensual;
      }
      if (estadoMes === "Atrasado") {
        const diasAtraso = Math.max(1, differenceInCalendarDays(HOY, parseISO(cobrosC[0].vencimiento)));
        punitorios = Math.round(c.montoMensual * 0.001 * diasAtraso);
      }
    } else if (c.situacion === "Con atraso" && i === 1) {
      estadoMes = "Pendiente";
      cobrado = 0;
      debe = c.montoMensual;
    }
    const arrInq = arreglos.filter((a) => a.aprobacion === "Aprobado" && a.paga === "Inquilino");
    if (i === 0 && arrInq.length) arr = arrInq[0].presupuesto;
    saldoAcumulado += debe + punitorios + arr;
    cuenta.push({ periodo: mesAnio(fechaMes), cobrado, debe, punitorios, arreglos: arr, saldo: saldoAcumulado, estado: estadoMes });
  }
  const saldoActual = saldoAcumulado;

  // ---------------- Historial de ajustes ----------------
  const historialAjustes = [];
  let montoPrevio = Math.round(c.montoMensual / 1.2 / 1000) * 1000;
  const nHist = entre(1, 3);
  for (let i = 0; i < nHist; i++) {
    const pct = c.indice === "Fijo" ? entre(8, 12) : entre(14, 30);
    historialAjustes.push({
      fecha: iso(addDays(HOY, -entre(90, 480))),
      pct,
      indice: c.indice,
      montoAnterior: montoPrevio,
      montoNuevo: i === nHist - 1 ? c.montoMensual : Math.round((montoPrevio * (1 + pct / 100)) / 1000) * 1000,
    });
    montoPrevio = historialAjustes[i].montoNuevo;
  }

  // ---------------- Costos y adicionales ----------------
  const abl = entre(9, 16) * 1000;
  const servicios = [
    { nombre: "Luz", paga: "Inquilino", alDia: true, monto: entre(28, 55) * 1000 },
    { nombre: "Gas", paga: "Inquilino", alDia: true, monto: entre(12, 30) * 1000 },
    { nombre: "Agua", paga: "Inquilino", alDia: c.situacion === "Con atraso" ? false : r() > 0.25, monto: entre(9, 18) * 1000 },
    { nombre: "Internet", paga: "Inquilino", alDia: true, monto: entre(20, 34) * 1000 },
  ];
  const expensasExtraordinarias =
    r() > 0.55
      ? { monto: entre(25, 90) * 1000, motivo: pick(["Trabajos en fachada", "Tanques de agua", "Reparación de ascensor", "Bomba de agua"]), paga: "Propietario" }
      : null;
  const seguroDepto = { paga: "Inquilino", monto: entre(18, 40) * 1000, alDia: true };
  const honorarios = { paga: "Propietario", monto: Math.round(c.montoMensual * 0.07) };
  const desgloseInquilino = [
    { concepto: "Alquiler", monto: c.montoMensual },
    { concepto: "Expensas ordinarias", monto: expensas },
    { concepto: "ABL", monto: abl },
    ...servicios.filter((s) => s.paga === "Inquilino").map((s) => ({ concepto: `Servicio · ${s.nombre}`, monto: s.monto })),
    { concepto: "Seguro del depto", monto: seguroDepto.monto },
  ];
  const totalMensualInquilino = desgloseInquilino.reduce((s, x) => s + x.monto, 0);

  // ---------------- Check-in ----------------
  const checkin = {
    fecha: c.inicio,
    llaves: entre(2, 4),
    medidores: {
      luz: `${entre(1200, 5400)} kWh`,
      gas: `${entre(320, 1200)} m³`,
      agua: `${entre(45, 140)} m³`,
    },
    inventario: AMBIENTES.map((amb, k) => ({
      ambiente: amb,
      foto: FOTOS[k % FOTOS.length],
      items: ["Paredes", "Piso", "Instalación eléctrica"].map((item) => ({
        item,
        estado: r() > 0.85 ? "Regular" : "Bueno",
      })),
    })),
    actaFirmada: true,
  };

  // ---------------- Check-out ----------------
  let checkout = null;
  if (c.estado === "En salida" || c.estado === "Finalizado") {
    const inspeccion = checkin.inventario.flatMap((inv) =>
      inv.items.map((it) => {
        const empeoro = r() > 0.72;
        const salida = empeoro ? (it.estado === "Bueno" ? "Regular" : "Malo") : it.estado;
        return {
          ambiente: inv.ambiente,
          item: it.item,
          entrada: it.estado,
          salida,
          accion: salida !== it.estado ? pick(["Repintar", "Reparar", "Cambiar pieza", "Limpieza a fondo"]) : "Sin cambios",
          costo: salida !== it.estado ? entre(40, 180) * 1000 : 0,
        };
      })
    );
    const costoArreglos = inspeccion.reduce((s, x) => s + x.costo, 0);
    const deuda = saldoActual > 0 ? saldoActual : 0;
    const descuentos = [
      ...(costoArreglos > 0 ? [{ concepto: "Arreglos pendientes", monto: costoArreglos }] : []),
      ...(deuda > 0 ? [{ concepto: "Deuda pendiente", monto: deuda }] : []),
    ];
    checkout = {
      aviso: { fecha: iso(addDays(HOY, -entre(10, 40))), quienAviso: pick(["Inquilino", "Propietario"]) },
      fechaPrevista: c.vencimiento,
      inspeccion,
      devolucion: {
        montoBase: deposito.monto,
        descuentos,
        saldo: Math.max(0, deposito.monto - descuentos.reduce((s, x) => s + x.monto, 0)),
      },
    };
  }

  // ---------------- Plazo ----------------
  const diasParaVencer = differenceInCalendarDays(parseISO(c.vencimiento), HOY);
  const duracionMeses = Math.max(1, Math.round(differenceInCalendarDays(parseISO(c.vencimiento), parseISO(c.inicio)) / 30));

  // ---------------- Línea de tiempo ----------------
  const timeline = [
    { fecha: c.inicio, tipo: "firma", texto: "Firma del contrato de locación" },
    { fecha: checkin.fecha, tipo: "entrada", texto: `Entrada al depto · entrega de ${checkin.llaves} juegos de llaves` },
    ...historialAjustes.map((a) => ({
      fecha: a.fecha,
      tipo: "ajuste",
      texto: `Ajuste ${a.indice} ${a.pct}% → $ ${a.montoNuevo.toLocaleString("es-AR")}`,
    })),
    ...[3, 2, 1].map((i) => ({
      fecha: iso(addDays(HOY, -30 * i - 2)),
      tipo: "pago",
      texto: `Pago de ${mesAnio(addDays(HOY, -30 * i))} registrado`,
    })),
    ...arreglos.map((a) => ({ fecha: a.fechaReporte, tipo: "arreglo", texto: `Arreglo reportado: ${a.titulo}` })),
  ];
  if (checkout) {
    timeline.push({ fecha: checkout.aviso.fecha, tipo: "aviso", texto: `Aviso de rescisión (avisó el ${checkout.aviso.quienAviso.toLowerCase()})` });
    if (c.estado === "Finalizado") {
      timeline.push({ fecha: c.vencimiento, tipo: "salida", texto: "Salida del depto y devolución de llaves" });
    }
  }
  timeline.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

  // ---------------- Documentos ----------------
  const documentos = [
    { id: "doc-contrato", nombre: "Contrato de locación", tipo: "Contrato" },
    { id: "doc-garantia", nombre: `Garantía (${garantia.tipo})`, tipo: "Garantía" },
    { id: "doc-acta-entrada", nombre: "Acta de entrega (entrada)", tipo: "Acta", acta: "entrada" },
  ];
  if (checkout) documentos.push({ id: "doc-acta-salida", nombre: "Acta de salida", tipo: "Acta", acta: "salida" });
  [0, 1, 2].forEach((i) =>
    documentos.push({ id: `doc-recibo-${i}`, nombre: `Recibo de alquiler · ${mesAnio(addDays(HOY, -30 * i))}`, tipo: "Recibo" })
  );
  arreglos.forEach((a) => documentos.push({ id: `doc-pres-${a.id}`, nombre: `Presupuesto · ${a.titulo}`, tipo: "Presupuesto" }));

  return {
    plazo: { inicio: c.inicio, fin: c.vencimiento, duracionMeses, diasParaVencer },
    pago: { diaPago: c.diaPago || "del 1 al 10", punitorios: "0,1% por día de mora" },
    garantia,
    deposito,
    historialAjustes,
    costos: { expensasOrdinarias: expensas, expensasExtraordinarias, abl, servicios, seguroDepto, honorarios, desgloseInquilino, totalMensualInquilino },
    cuenta,
    saldoActual,
    checkin,
    checkout,
    arreglos,
    timeline,
    documentos,
  };
}

// ---------------------- Alertas para el listado ----------------------

export function alertasContrato(c) {
  const d = detalleContrato(c);
  const cobrosC = COBROS.filter((x) => x.contratoId === c.id);
  const alertas = [];
  if (cobrosC.some((x) => x.estado === "Atrasado")) alertas.push({ texto: "Pago atrasado", tone: "red" });
  const dAj = differenceInCalendarDays(parseISO(c.proximaActualizacion), HOY);
  if (dAj >= 0 && dAj <= 30) alertas.push({ texto: "Ajusta este mes", tone: "blue" });
  const dV = differenceInCalendarDays(parseISO(c.vencimiento), HOY);
  if (c.estado !== "Finalizado" && dV >= 0 && dV <= 60) alertas.push({ texto: "Vence en 60 días", tone: "yellow" });
  if (d.arreglos.some((a) => a.aprobacion === "Pendiente")) alertas.push({ texto: "Arreglo sin aprobar", tone: "purple" });
  return alertas;
}

export function arreglosAbiertos(c) {
  return detalleContrato(c).arreglos.filter((a) => a.estado !== "Terminado").length;
}
